import { useMemo } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";

// World atlas TopoJSON (countries 110m resolution)
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Map country names from world-atlas Natural Earth dataset → ISO alpha-2
// Only entries that may differ from our DB country names are listed; otherwise name match works.
const NAME_TO_ALPHA2 = {
  "United States of America": "US",
  "United Kingdom": "GB",
  "Germany": "DE",
  "France": "FR",
  "Italy": "IT",
  "Spain": "ES",
  "Netherlands": "NL",
  "Sweden": "SE",
  "Japan": "JP",
  "Australia": "AU",
  "Canada": "CA",
  "South Korea": "KR",
  "China": "CN",
  "Brazil": "BR",
  "Ireland": "IE",
  "Switzerland": "CH",
  "Russia": "RU",
  "India": "IN",
  "Mexico": "MX",
  "Argentina": "AR",
  "Norway": "NO",
  "Denmark": "DK",
  "Finland": "FI",
  "Poland": "PL",
  "Belgium": "BE",
  "Austria": "AT",
  "Portugal": "PT",
  "Greece": "GR",
  "Turkey": "TR",
  "South Africa": "ZA",
  "New Zealand": "NZ",
};

export default function WorldMap({ availableCountryCodes = [], selectedCountry = "all", onCountryClick }) {
  const availableSet = useMemo(() => new Set(availableCountryCodes), [availableCountryCodes]);

  const styleFor = (alpha2) => {
    const isAvailable = availableSet.has(alpha2);
    const isSelected = selectedCountry === alpha2;
    if (isSelected) {
      return {
        default: { fill: "#7DA3D9", stroke: "#B8D0EE", strokeWidth: 0.6, outline: "none" },
        hover: { fill: "#B8D0EE", stroke: "#B8D0EE", strokeWidth: 0.8, outline: "none", cursor: "pointer" },
        pressed: { fill: "#B8D0EE", outline: "none" },
      };
    }
    if (isAvailable) {
      return {
        default: { fill: "rgba(212,175,55,0.35)", stroke: "rgba(212,175,55,0.6)", strokeWidth: 0.5, outline: "none" },
        hover: { fill: "#7DA3D9", stroke: "#B8D0EE", strokeWidth: 0.7, outline: "none", cursor: "pointer" },
        pressed: { fill: "#7DA3D9", outline: "none" },
      };
    }
    return {
      default: { fill: "rgba(255,255,255,0.04)", stroke: "rgba(255,255,255,0.1)", strokeWidth: 0.4, outline: "none" },
      hover: { fill: "rgba(255,255,255,0.08)", stroke: "rgba(255,255,255,0.2)", strokeWidth: 0.5, outline: "none" },
      pressed: { fill: "rgba(255,255,255,0.08)", outline: "none" },
    };
  };

  return (
    <div className="relative w-full" data-testid="world-map">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: 130, center: [10, 25] }}
        width={980}
        height={460}
        style={{ width: "100%", height: "auto", background: "transparent" }}
      >
        <ZoomableGroup zoom={1} minZoom={1} maxZoom={4} center={[10, 25]}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const name = geo.properties.name;
                const alpha2 = NAME_TO_ALPHA2[name];
                const clickable = alpha2 && availableSet.has(alpha2);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    style={styleFor(alpha2)}
                    onClick={() => {
                      if (clickable && onCountryClick) onCountryClick(alpha2, name);
                    }}
                    data-testid={alpha2 ? `map-country-${alpha2}` : undefined}
                  >
                    <title>{name}{clickable ? " — click to filter" : ""}</title>
                  </Geography>
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-5 mt-4 text-xs text-slate-400" data-testid="map-legend">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm bg-[#7DA3D9]" />
          <span>Selected territory</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm" style={{ background: "rgba(212,175,55,0.35)", border: "1px solid rgba(212,175,55,0.6)" }} />
          <span>Has registered agencies — click to filter</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)" }} />
          <span>Not yet indexed</span>
        </div>
      </div>
    </div>
  );
}
